/**
 * ホームの司書の吹き出しの「ふだんの一言」。管理UIの「司書の一言」（/librarian-lines）で足す・直す・消す（下書き→公開）。
 * 正本は Firestore の appSettings/librarianLines（公開版）。管理UIは local-ui/scripts/sync-shared.mjs で写したものを読む。
 *
 * 吹き出しをタップすると次の一言へ切り替わる。「先頭に出す」一言は、ホームを初めて開いたときに一覧の順で先に出す。
 * 一言には行き先のボタン（「書庫を開く →」など）を付けられる。
 *
 * 前は「読み方の選び忘れ」「まだ開いていない本」の知らせを決まった知らせとして出していた（文は「文言」の home.note.*）。
 * 管理UIで一覧を開くとこの2つを一覧の先頭に移し、公開版に noticesInList: true が付く。付いていない公開版（移す前）では、
 * 今までどおり決まった知らせを先に出す。一言の一覧が空・未公開なら「文言」の home.note.default を出す。
 *
 * 一言には {引ける冊数} のような差し込みを書ける（魔力などから計算した値が入る）。
 * 値が出せない差し込み（魔力の配り方がまだ届いていない など）を含む一言は、その時は出さない。
 *
 * 一言ごとに条件を1つ付けられる。数で決める条件（差し込みの値と数を比べる）か、状態で決める条件（今日の読み方を選んだか）。
 * 条件に合うとき「出す」か「出さない」かを選ぶ（hide が無ければ出す）。出さないときは「代わりの一言」を出し、
 * 代わりの一言が無ければその一言は出さない。
 */
import { GACHA_ENTRY_PATH } from "./gacha-entry";

export const LIBRARIAN_LINE_OPS = {
  gte: "以上",
  lte: "以下",
  eq: "ちょうど",
} as const;
export type LibrarianLineOp = keyof typeof LIBRARIAN_LINE_OPS;

/** 状態で決める条件の名前と、管理UIに出す説明 */
export const LIBRARIAN_LINE_FLAGS = {
  読み方を選んでいない: "今日の読み方をまだ選んでいないとき",
  読み方を選んだ: "今日の読み方を選んだとき",
  読み方を選び忘れて行動した: "今日の読み方を選ばないまま、本を読むなどしたとき",
} as const;
export type LibrarianLineFlag = keyof typeof LIBRARIAN_LINE_FLAGS;

/** 状態。分からない（まだ読み込んでいない）ものは入れない */
export interface LibrarianLineFacts {
  読み方を選んだ?: boolean;
  /** 読み方を選ばないまま、ミッションで数える行動をした */
  読み方を選び忘れて行動した?: boolean;
}

/** 一言に付けられる行き先のボタン。管理UIで選ぶ */
export const LIBRARIAN_LINE_LINKS = {
  shelf: { label: "書庫", href: "/shelf" },
  quests: { label: "今日の読み方", href: "/quests" },
  gacha: { label: "本を引く", href: GACHA_ENTRY_PATH },
  map: { label: "地図", href: "/map" },
  party: { label: "精霊", href: "/party" },
} as const;
export type LibrarianLineLinkTo = keyof typeof LIBRARIAN_LINE_LINKS;
export const LIBRARIAN_LINK_LABEL_MAX = 20;

export interface LibrarianLineLink {
  to: LibrarianLineLinkTo;
  /** ボタンの文（後ろに → が付く） */
  label: string;
}

export type LibrarianLineCondition = (
  | { var: LibrarianLineVar; op: LibrarianLineOp; value: number }
  | { flag: LibrarianLineFlag }
) & {
  /** 条件に合うときは出さない（無ければ、条件に合うときに出す） */
  hide?: boolean;
};

export interface LibrarianLine {
  id: string;
  text: string;
  /** 管理UIで「出す」を外した一言 */
  hidden?: boolean;
  /** 条件。無ければいつでも出す */
  condition?: LibrarianLineCondition;
  /** この一言を出さないときに、代わりに出す一言。無ければ何も出さない */
  fallback?: string;
  /** ホームを初めて開いたとき、混ぜずに先に出す（一覧の順） */
  first?: boolean;
  /** 一言の下に出す行き先のボタン（代わりの一言のときは出さない） */
  link?: LibrarianLineLink;
}

/** 吹き出しに出す話（文と、あれば行き先のボタン） */
export interface LibrarianSpeech {
  text: string;
  link: LibrarianLineLink | null;
  first: boolean;
}

export const LIBRARIAN_LINE_MAX = 80;
export const LIBRARIAN_LINES_MAX_COUNT = 200;

/** 差し込みの名前と、管理UIに出す説明・見本の値 */
export const LIBRARIAN_LINE_VARS = {
  魔力: { label: "いまの魔力（無償＋有償）", sample: 895 },
  引ける冊数: { label: "いまの魔力で今日あと何冊引けるか（本は今日引くほど高くなる）", sample: 6 },
  "1冊の魔力": { label: "次の1冊を引くのに要る魔力（今日引くほど高くなる）", sample: 10 },
  上限: { label: "無償の魔力をためておける上限", sample: 600 },
  上限まで: { label: "無償の魔力が上限まであと何pt か（あふれる前に使う目安）", sample: 0 },
  回復の量: { label: "毎日届く魔力の量", sample: 60 },
  回復の時刻: { label: "魔力が届く時刻（時）", sample: 4 },
  レベル: { label: "いまのレベル", sample: 3 },
  次のレベルまで: { label: "次のレベルまで、あと何冊読み終えるか（最後のレベルでは出さない）", sample: 15 },
  読み終えた冊数: { label: "これまでに初めて読み終えた冊数", sample: 42 },
  未読の冊数: { label: "まだ開いていない本の冊数", sample: 3 },
} as const;

export type LibrarianLineVar = keyof typeof LIBRARIAN_LINE_VARS;
export type LibrarianLineValues = Partial<Record<LibrarianLineVar, number>>;

export const LIBRARIAN_LINE_SAMPLE_VALUES: LibrarianLineValues = Object.fromEntries(
  Object.entries(LIBRARIAN_LINE_VARS).map(([k, v]) => [k, v.sample]),
) as LibrarianLineValues;

/** 一言の中の差し込みの名前（知らない名前も含めてそのまま返す） */
export function lineVarNames(text: string): string[] {
  return [...text.matchAll(/\{([^{}]+)\}/g)].map((m) => m[1]);
}

/** 一言に差し込みを入れる。知らない名前や値の無い差し込みが一つでもあれば null（その一言は出さない） */
export function fillLibrarianLine(text: string, values: LibrarianLineValues): string | null {
  let missing = false;
  const filled = text.replace(/\{([^{}]+)\}/g, (_all, name: string) => {
    const v = values[name as LibrarianLineVar];
    if (typeof v !== "number" || !Number.isFinite(v)) {
      missing = true;
      return "";
    }
    return String(v);
  });
  return missing ? null : filled;
}

/** 条件に合うか。値が出せない差し込み・分からない状態の条件は null（合うとも合わないとも決めない） */
export function meetsLibrarianCondition(
  condition: LibrarianLineCondition,
  values: LibrarianLineValues,
  facts: LibrarianLineFacts = {},
): boolean | null {
  if ("flag" in condition) {
    if (condition.flag === "読み方を選び忘れて行動した") {
      const forgot = facts.読み方を選び忘れて行動した;
      return typeof forgot === "boolean" ? forgot : null;
    }
    const chosen = facts.読み方を選んだ;
    if (typeof chosen !== "boolean") return null;
    return condition.flag === "読み方を選んだ" ? chosen : !chosen;
  }
  const v = values[condition.var];
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  if (condition.op === "gte") return v >= condition.value;
  if (condition.op === "lte") return v <= condition.value;
  return v === condition.value;
}

/**
 * いま出す文。出すときは一言、出さないときは代わりの一言（無ければ null）。
 * 条件の値が出せないとき・差し込みが埋まらないときも null（その一言は出さない）
 */
export function resolveLibrarianLine(line: LibrarianLine, values: LibrarianLineValues, facts: LibrarianLineFacts = {}): string | null {
  if (!line.condition) return fillLibrarianLine(line.text, values);
  const met = meetsLibrarianCondition(line.condition, values, facts);
  if (met === null) return null;
  const show = line.condition.hide ? !met : met;
  if (show) return fillLibrarianLine(line.text, values);
  return line.fallback ? fillLibrarianLine(line.fallback, values) : null;
}

/** いま出す話。代わりの一言のときは行き先のボタンを付けない。出さないときは null */
export function resolveLibrarianSpeech(line: LibrarianLine, values: LibrarianLineValues, facts: LibrarianLineFacts = {}): LibrarianSpeech | null {
  let show = true;
  if (line.condition) {
    const met = meetsLibrarianCondition(line.condition, values, facts);
    if (met === null) return null;
    show = line.condition.hide ? !met : met;
  }
  const text = show ? fillLibrarianLine(line.text, values) : line.fallback ? fillLibrarianLine(line.fallback, values) : null;
  if (text === null) return null;
  return { text, link: show && line.link ? line.link : null, first: !!line.first };
}

/**
 * 決まった知らせ（読み方の選び忘れ・まだ開いていない本）を、一覧の一言にしたもの。管理UIで一覧へ移すときに使う。
 * 文は「文言」の home.note.* のいまの文（差し込み {冊数} は {未読の冊数} へ）
 */
export function noticeLibrarianLines(texts: { forgotDaily: string; chooseDaily: string; unread: string; openShelf: string }): LibrarianLine[] {
  return [
    {
      id: "notice_forgot_daily",
      text: texts.forgotDaily.slice(0, LIBRARIAN_LINE_MAX),
      condition: { flag: "読み方を選び忘れて行動した" },
      first: true,
      link: { to: "quests", label: texts.chooseDaily.slice(0, LIBRARIAN_LINK_LABEL_MAX) },
    },
    {
      id: "notice_unread",
      text: texts.unread.replaceAll("{冊数}", "{未読の冊数}").slice(0, LIBRARIAN_LINE_MAX),
      condition: { var: "未読の冊数", op: "gte", value: 1 },
      first: true,
      link: { to: "shelf", label: texts.openShelf.slice(0, LIBRARIAN_LINK_LABEL_MAX) },
    },
  ];
}

/** 行き先のボタンを読む。知らない行き先なら undefined。ボタンの文が空なら行き先の名前 */
export function parseLibrarianLink(data: unknown): LibrarianLineLink | undefined {
  const l = data as { to?: unknown; label?: unknown } | null | undefined;
  if (!l || typeof l.to !== "string" || !(l.to in LIBRARIAN_LINE_LINKS)) return undefined;
  const to = l.to as LibrarianLineLinkTo;
  const label = typeof l.label === "string" ? l.label.trim().slice(0, LIBRARIAN_LINK_LABEL_MAX) : "";
  return { to, label: label || LIBRARIAN_LINE_LINKS[to].label };
}

/** 条件を読む。知らない差し込み・状態・比べ方・数でない値なら undefined（条件なし） */
export function parseLibrarianCondition(data: unknown): LibrarianLineCondition | undefined {
  const f = data as { flag?: unknown; hide?: unknown } | null | undefined;
  const hide = f?.hide === true ? { hide: true } : {};
  if (f && typeof f.flag === "string") {
    return f.flag in LIBRARIAN_LINE_FLAGS ? { flag: f.flag as LibrarianLineFlag, ...hide } : undefined;
  }
  const c = data as { var?: unknown; op?: unknown; value?: unknown } | null | undefined;
  if (!c || typeof c.var !== "string" || !(c.var in LIBRARIAN_LINE_VARS)) return undefined;
  if (typeof c.op !== "string" || !(c.op in LIBRARIAN_LINE_OPS)) return undefined;
  const value = Number(c.value);
  if (!Number.isFinite(value)) return undefined;
  return { var: c.var as LibrarianLineVar, op: c.op as LibrarianLineOp, value: Math.round(value), ...hide };
}

/** 公開版の文書に、決まった知らせを一覧へ移した印があるか */
export function librarianNoticesInList(data: unknown): boolean {
  return (data as { noticesInList?: unknown } | undefined)?.noticesInList === true;
}

/** Firestore の値を読む。読めない一言・空の一言は捨て、長すぎる文は切る。一つも無ければ空の配列 */
export function parseLibrarianLines(data: unknown): LibrarianLine[] {
  const raw = (data as { lines?: unknown } | undefined)?.lines;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: LibrarianLine[] = [];
  for (const item of raw) {
    const line = item as Partial<LibrarianLine> | null;
    if (!line || typeof line.id !== "string" || !line.id || seen.has(line.id)) continue;
    if (typeof line.text !== "string") continue;
    const text = line.text.trim().slice(0, LIBRARIAN_LINE_MAX);
    if (!text) continue;
    seen.add(line.id);
    const condition = parseLibrarianCondition(line.condition);
    const fallback = condition && typeof line.fallback === "string" ? line.fallback.trim().slice(0, LIBRARIAN_LINE_MAX) : "";
    const link = parseLibrarianLink(line.link);
    out.push({
      id: line.id,
      text,
      ...(line.hidden ? { hidden: true } : {}),
      ...(condition ? { condition } : {}),
      ...(fallback ? { fallback } : {}),
      ...(line.first ? { first: true } : {}),
      ...(link ? { link } : {}),
    });
    if (out.length >= LIBRARIAN_LINES_MAX_COUNT) break;
  }
  return out;
}
